import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { Observable, Subject, Subscription, fromEvent, interval, timer } from 'rxjs';
import { map, shareReplay, tap, takeUntil, take, filter as rxFilter } from 'rxjs/operators';
import { ProjectService } from './Modules/Projects/project.service';

import { Project } from './shared/Models/project-model';
import { OAuthService } from './shared/services/oauth.service';
import { SnackBarService } from './shared/services/snack-service.service';
import { AppVersionService } from './shared/services/app-version.service';
import { DebuggerService } from './shared/services/debugger.service';
import { LoggerService } from './shared/services/logger.service';
import { TranslateService } from '@ngx-translate/core';
import { PermissionConstants as Permissions } from 'src/app/constants/permissions';
import { Store } from '@ngrx/store';
import { AppActions } from './state/app.actions';
import {
  selectCurrentProjectId,
  selectCurrentUser,
  selectIsLoading,
  selectSelectableProjects,
} from './state/app.selectors';
import { MatSidenav } from '@angular/material/sidenav';
import { LicenseService } from './shared/services/license.service';
import { LicenseDialogComponent } from './core/license/license.component';
import { MatDialog } from '@angular/material/dialog';
import { SwUpdate } from '@angular/service-worker';
import { AppReloadService } from './shared/services/app-reload.service';
import { UpdateNoticeService } from './shared/services/update-notice.service';
import { AboutDialogComponent } from './shared/components/about-dialog/about-dialog.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'RaksaInfo®';

  isHandset$: Observable<boolean> = this.breakpointObserver
    .observe([Breakpoints.Handset, Breakpoints.Small])
    .pipe(
      map((result) => result.matches),
      shareReplay()
    );

  username: string = '';
  // deprecated imperative flag kept only for the sidenav login button guard
  isLoggedIn: boolean = false;
  loginState$ = this._authService.loginChanged;
  loginReady = false;
  selectedProject$ = this.store.select(selectCurrentProjectId);
  projectsLoading$ = this.store.select(selectIsLoading);
  projects: Project[] = [];
  showDebug: boolean = false;
  langIcon = 'fi-fi';
  permissions = Permissions;
  private debugSubscription!: Subscription;
  private readonly destroy$ = new Subject<void>();
  private updateBannerOpen = false;
  private updateReadyHandled = false;
  navLinks = [
    { path: '/', label: 'NAVBAR.HOME', icon: 'home' },
  ];

  @ViewChild('drawer') drawer!: MatSidenav;

  selectedTheme: 'm3-light' | 'm3-dark' = (() => {
    const stored = localStorage.getItem('theme');
    if (stored === 'm2') {
      localStorage.setItem('theme', 'm3-light');
      return 'm3-light';
    }
    return (stored as 'm3-light' | 'm3-dark') || 'm3-light';
  })();

  constructor(
    public _authService: OAuthService,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private projectService: ProjectService,
    private store: Store,
    private snackService: SnackBarService,
    private debugService: DebuggerService,
    private translate: TranslateService,
    private licenseService: LicenseService,
    private dialog: MatDialog,
    private swUpdate: SwUpdate,
    private logger: LoggerService,
    private appVersion: AppVersionService,
    private updateNotice: UpdateNoticeService,
    private appReload: AppReloadService
  ) {
    const persisted = localStorage.getItem('language');
    const supported = ['fi', 'en', 'gb', 'ee'];
    const initialLang = (persisted && supported.includes(persisted)) ? persisted : 'fi';
    this.translate.setFallbackLang('fi');
    this.switchLanguage(initialLang);
  }

  closeDrawerOnHandset(): void {
    // isHandset$ replays synchronously, so the callback runs before subscribe() returns —
    // take(1) instead of unsubscribing a `sub` that isn't assigned yet (ReferenceError).
    this.isHandset$.pipe(take(1)).subscribe(isHandset => {
      if (isHandset) {
        this.drawer.close();
      }
    });
  }

  toggleDebugMode(event: { checked: boolean }) {
    this.showDebug = event.checked;
    this.debugService.setDebugMode(event.checked);
  }

  versionLine$ = this.appVersion.versionLine$;
  copyrightYear = this.appVersion.copyrightYear();

  openAboutDialog(): void {
    this.dialog.open(AboutDialogComponent, { width: '480px', maxWidth: '92vw' });
  }

  ngOnInit() {
    this.appVersion.announceUpdateIfNeeded(() => this.openAboutDialog());
    this.store.select(selectCurrentUser).subscribe((user) => {
      this.username = user?.profile.preferred_username || '';
    });
    this.store.select(selectSelectableProjects).subscribe((projects) => {
      this.projects = projects;
    });
    this.debugSubscription = this.debugService.debugMode$.subscribe((mode) => {
      this.showDebug = mode;
    });
    this.checkForAppUpdates();
    this.loginState$.pipe(
      tap(async (loggedIn) => {
        const wasReady = this.loginReady;
        this.loginReady = true;
        this.isLoggedIn = loggedIn;

        if (!loggedIn) {
          if (wasReady) {
            // Close any open Material dialogs (add/edit hours, confirmations, …). They live in
            // an overlay that router navigation doesn't touch, so without this an add-hours
            // dialog stays floating over /session-expired — the user can still hit Save, firing
            // a request that 401s with a dead token and silently loses their entry.
            this.dialog.closeAll();
            this.store.dispatch(AppActions.clearCurrentUser());
            this.router.navigate(['/session-expired'], { replaceUrl: true });
          }
          return;
        }

        if (wasReady && this.router.url === '/session-expired') {
          this.router.navigate(['/'], { replaceUrl: true });
        }

      })
    ).subscribe({
      error: (err) => {
        this.loginReady = true;
        this.logger.errorWithContext('AppComponent', 'Login state stream error', err);
      },
    });
    this.applyTheme(this.selectedTheme);
  }

  ngOnDestroy(): void {
    this.debugSubscription?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  login() {
    this._authService.login();
  }

  logout() {
    this._authService.logout();
  }

  changeProject(event: number) {
    this.projectService.updateUserProject(event).subscribe(() => {
      const changedMessage = this.translate.instant('PROJECTCHANGEDMESSAGE');
      this.snackService.openSnackBar(changedMessage, 'OK', 5000);
    });
  }

  openLicenseDialog(): void {
    this.licenseService.getLicenses().subscribe(licenses => {
      this.dialog.open(LicenseDialogComponent, {
        width: '600px',
        maxWidth: '90vw',
        maxHeight: '90vh',
        data: { licenses }
      });
    });
  }

  onThemeChange(theme: 'm3-light' | 'm3-dark') {
    this.selectedTheme = theme;
    localStorage.setItem('theme', theme);
    this.applyTheme(theme);
  }

  applyTheme(theme: 'm3-light' | 'm3-dark') {
    const body = document.body;
    body.classList.remove('m2-theme', 'm3-light-theme', 'm3-dark-theme');
    body.classList.add(theme + '-theme');
  }

  switchLanguage(language: string) {
    this.translate.use(language);
    localStorage.setItem('language', language);
    this.langIcon = language === 'en' ? 'fi-gb' : 'fi-' + language;
  }

  testErrorHandler() {
    throw new Error('Test error from analytics error handler - this should be logged to analytics!');
  }

  private checkForAppUpdates(): void {
    if (!this.swUpdate.isEnabled) return;

    // Force reload if SW loses track of cached chunks — no recovery possible
    this.swUpdate.unrecoverable
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.reloadForNewVersion(window.location.pathname + window.location.search));

    this.swUpdate.versionUpdates
      .pipe(takeUntil(this.destroy$))
      .subscribe(evt => {
        if (evt.type === 'VERSION_READY') {
          // Do NOT call activateUpdate() here. Activating reassigns this running
          // tab to the new asset version while the old shell is still executing —
          // its next lazy-load then requests old-hash chunks the new manifest no
          // longer has, producing network 404s and mixed old/new modules
          // (observed in prod 2026-08-04). A full page load switches versions
          // atomically; until then the SW keeps serving the old version intact.
          this.onUpdateReady();
        } else if (evt.type === 'VERSION_INSTALLATION_FAILED') {
          this.logger.errorWithContext('AppComponent', 'SW version installation failed', evt);
        }
      });

    fromEvent(document, 'visibilitychange')
      .pipe(rxFilter(() => !document.hidden), takeUntil(this.destroy$))
      .subscribe(() => this.swUpdate.checkForUpdate());

    interval(5 * 60 * 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.swUpdate.checkForUpdate());
  }

  private onUpdateReady(): void {
    // A tab left open across several deploys receives VERSION_READY once per deploy.
    // One intercept is enough: the reload lands on whatever version is latest. Stacking
    // them made every intercept fire on the same NavigationStart, and all but the first
    // tripped the repeat-reload guard (prod, 2026-09-30).
    if (this.updateReadyHandled) return;
    this.updateReadyHandled = true;

    const nextNav$ = this.router.events.pipe(
      rxFilter((e): e is NavigationStart => e instanceof NavigationStart),
      // Never the initial navigation. That one belongs to a document the browser has
      // *just* loaded, so reloading it onto itself buys nothing — and because we
      // deliberately never call activateUpdate(), the reloaded page can report
      // VERSION_READY again and reload again. Landing on /signin-callback that
      // reloads the page while finishLogin() is exchanging the code, which destroys
      // the signin state and surfaces as "No matching state found in storage"
      // (prod, 2026-08-31 and 2026-09-01).
      rxFilter(event => event.id > 1),
      // Same reason for any later navigation: an OIDC callback must be allowed to
      // finish. The subscription stays open, so the user still picks up the new
      // version on their next navigation once they have left the callback route.
      rxFilter(() => !this.isAuthCallbackRoute(this.router.url)),
      take(1)
    );

    // Convert the user's next SPA navigation into a full-page load so they
    // seamlessly land on the intended route with fresh bundles — no form state lost
    nextNav$.pipe(takeUntil(this.destroy$)).subscribe(e => {
      this.reloadForNewVersion(e.url);
    });

    // Fallback: if they stay parked on one page for 5 min, prompt explicitly —
    // parked screens (duty roster, dashboards) never navigate, so a long delay
    // just leaves them on the old version. Waiting is safe (the SW keeps serving
    // the old version intact), and the navigation intercept above stays active
    // regardless — even if they dismiss the banner, their next route click
    // still triggers the reload.
    timer(5 * 60 * 1000)
      .pipe(takeUntil(nextNav$), takeUntil(this.destroy$))
      .subscribe(() => this.showUpdateBanner());
  }

  /** Routes where a full-page load would destroy an in-flight OIDC exchange. */
  private isAuthCallbackRoute(url: string): boolean {
    return ['/signin-callback', '/signout-callback', '/silent-callback'].some(route => url.startsWith(route));
  }

  /**
   * Full page load onto `url` so the user lands on the intended route running the new
   * bundles.
   *
   * Guarded: if we already did this within the last 30 seconds then the reload is not
   * actually switching versions and something is re-triggering it, so stop rather than
   * loop. Both callers (`unrecoverable`, and the navigation intercept above) were
   * previously unguarded, which is what let a reload loop run on the signin callback.
   */
  private reloadForNewVersion(url: string): void {
    this.appReload.reloadForNewVersion(url);
  }

  private showUpdateBanner(): void {
    if (this.updateBannerOpen) return;

    this.updateBannerOpen = true;
    // Held back while a field has focus (never interrupts typing).
    this.updateNotice.showWhenIdle(
      'update',
      // "Päivitä nyt" goes through the same guarded path as the navigation intercept, so
      // the 30 s repeat-reload guard applies here too.
      () => this.reloadForNewVersion(this.router.url),
      () => {
        this.updateBannerOpen = false;
        // Navigation intercept in onUpdateReady() remains active —
        // next route click will still trigger the full-page reload
      },
      this.destroy$
    );
  }
}
